import { useEffect, useState } from 'react';

type UserAgentInfo = {
  userAgentData: {
    platform: string;
    mobile: boolean;
    brands: { brand: string; version: string }[];
  };
};

type NavigatorWithConnection = Navigator & {
  connection?: {
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
    saveData?: boolean;
  };
};

export type BrowserStats = {
  userAgent: string;
  screenMetrics?: {
    screenWidth: number;
    screenHeight: number;
    availWidth: number;
    availHeight: number;
    colorDepth: number;
    pixelDepth: number;
  };
  viewportMetrics?: {
    viewportWidth: number;
    viewportHeight: number;
    scrollX: number;
    scrollY: number;
    documentWidth: number;
    documentHeight: number;
  };
  navigatorMetrics?: {
    userAgent: string;
    platform: string;
    deviceType: string;
    brands: { brand: string; version: string }[];
    language: string;
    languages: string[];
    cookieEnabled: boolean;
    online: boolean;
    hardwareConcurrency: number;
    deviceMemory?: number;
    maxTouchPoints: number;
  };
  connectionMetrics?: {
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
    saveData?: boolean;
  };
  performanceMetrics?: {
    navigationStart: number;
    domContentLoaded: number;
    loadEventEnd: number;
    redirectCount: number;
    type: string;
    pageLoadTime: number;
  };
  browserInfo?: {
    browserName: string;
  };
  gpuInfo?: {
    vendor?: string;
    renderer?: string;
    error?: string;
  };
};

export function useBrowserStats() {
  const [browserStats, setBrowserStats] = useState<BrowserStats | null>(null);

  useEffect(() => {
    async function printFullBrowserMetrics() {
      // ---- Screen Metrics ----
      const screenMetrics = {
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        availWidth: window.screen.availWidth,
        availHeight: window.screen.availHeight,
        colorDepth: window.screen.colorDepth,
        pixelDepth: window.screen.pixelDepth,
      };

      // ---- Viewport Metrics ----
      const viewportMetrics = {
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        scrollX: window.scrollX,
        scrollY: window.scrollY,
        documentWidth: document.documentElement.scrollWidth,
        documentHeight: document.documentElement.scrollHeight,
      };

      // ---- Navigator / Browser Info ----
      let platform = 'Unknown';
      let deviceType = 'Unknown';
      let brands: { brand: string; version: string }[] = [];

      // Modern approach using Client Hints (if available)
      if ('userAgentData' in navigator && (navigator as UserAgentInfo).userAgentData?.platform) {
        platform = (navigator as UserAgentInfo).userAgentData.platform; // "Windows", "macOS", "Android", etc.
        deviceType = (navigator as UserAgentInfo).userAgentData.mobile ? 'Mobile' : 'Desktop';
        brands = (navigator as UserAgentInfo).userAgentData.brands || [];
      } else {
        // Fallback: basic OS detection from userAgent
        const ua = navigator.userAgent;
        if (/Windows NT/i.test(ua)) platform = 'Windows';
        else if (/Macintosh|MacIntel|MacPPC|Mac68K/i.test(ua)) platform = 'macOS';
        else if (/Linux/i.test(ua)) platform = 'Linux';
        else if (/Android/i.test(ua)) platform = 'Android';
        else if (/iPhone|iPad|iPod/i.test(ua)) platform = 'iOS';
      }

      const navigatorMetrics = {
        userAgent: navigator.userAgent,
        platform,
        deviceType,
        brands,
        language: navigator.language,
        languages: [...navigator.languages],
        cookieEnabled: navigator.cookieEnabled,
        online: navigator.onLine,
        hardwareConcurrency: navigator.hardwareConcurrency,
        deviceMemory: 'deviceMemory' in navigator ? (navigator as { deviceMemory: number }).deviceMemory : undefined,
        maxTouchPoints: navigator.maxTouchPoints,
      };

      // ---- Network Info ----
      let connectionMetrics: BrowserStats['connectionMetrics'] = undefined;
      if ((navigator as NavigatorWithConnection).connection) {
        const connection = (navigator as NavigatorWithConnection).connection!;

        connectionMetrics = {
          effectiveType: connection.effectiveType,
          downlink: connection.downlink,
          rtt: connection.rtt,
          saveData: connection.saveData,
        };
      }

      // ---- Performance Timing (modern) ----
      let performanceMetrics: BrowserStats['performanceMetrics'] = undefined;
      const [navTiming] = performance.getEntriesByType('navigation');
      if (navTiming) {
        const navTimingEntry = navTiming as PerformanceNavigationTiming;
        performanceMetrics = {
          navigationStart: navTimingEntry.startTime,
          domContentLoaded: navTimingEntry.domContentLoadedEventEnd,
          loadEventEnd: navTimingEntry.loadEventEnd,
          redirectCount: navTimingEntry.redirectCount,
          type: navTimingEntry.type,
          pageLoadTime: navTimingEntry.loadEventEnd - navTimingEntry.startTime,
        };
      }

      // ---- Detect Browser Type ----
      function detectBrowser() {
        const ua = navigator.userAgent;
        if (/firefox/i.test(ua)) return 'Firefox';
        if (/chrome|crios/i.test(ua) && !/edg/i.test(ua)) return 'Chrome';
        if (/safari/i.test(ua) && !/chrome|crios/i.test(ua)) return 'Safari';
        if (/edg/i.test(ua)) return 'Edge';
        if (/opr|opera/i.test(ua)) return 'Opera';
        return 'Unknown';
      }

      const browserInfo = {
        browserName: detectBrowser(),
      };

      // ---- GPU Info ----
      let gpuInfo: BrowserStats['gpuInfo'] = undefined;
      try {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        if (gl) {
          const webglContext = gl as WebGLRenderingContext;
          const debugInfo = webglContext.getExtension('WEBGL_debug_renderer_info');
          gpuInfo = {
            vendor: debugInfo ? webglContext.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : 'Unknown',
            renderer: debugInfo ? webglContext.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : 'Unknown',
          };
        } else {
          gpuInfo = { vendor: 'WebGL not supported', renderer: 'WebGL not supported' };
        }
      } catch (err) {
        gpuInfo = { error: err instanceof Error ? err.message : 'Unknown error' };
      }

      setBrowserStats({
        userAgent: navigator.userAgent,
        screenMetrics,
        viewportMetrics,
        navigatorMetrics,
        connectionMetrics,
        performanceMetrics,
        browserInfo,
        gpuInfo,
      });
    }

    // Run the function
    printFullBrowserMetrics();

    window.addEventListener('resize', printFullBrowserMetrics);

    return () => {
      window.removeEventListener('resize', printFullBrowserMetrics);
    };
  }, []);

  return browserStats;
}
